import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import {
    Alert,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    TextField,
    Tooltip
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import PostAddIcon from "@mui/icons-material/PostAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter} from "@mui/x-data-grid";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
}

/** Parse a CSV file text into rows of trimmed string arrays, skipping the header. */
function parseCsv(text: string): string[][] {
    return text
        .split(/\r?\n/)
        .slice(1)
        .filter(line => line.trim().length > 0)
        .map(line => line.split(',').map(cell => cell.trim()))
}

function QuickFilterToolBar({onAdd, onUpload}: {onAdd: () => void, onUpload: (file: File) => void}) {
    const inputRef = useRef<HTMLInputElement>(null)
    return (
        <GridToolbarContainer>
            <GridToolbarQuickFilter/>
            <Tooltip title="Add ability">
                <Button onClick={onAdd}><PostAddIcon/></Button>
            </Tooltip>
            <Tooltip title="Import from CSV">
                <Button component="label">
                    <UploadFileIcon/>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv"
                        hidden
                        onChange={e => {
                            if (e.target.files?.[0]) {
                                onUpload(e.target.files[0])
                                e.target.value = ''
                            }
                        }}
                    />
                </Button>
            </Tooltip>
        </GridToolbarContainer>
    )
}

// ── Offensive Abilities ──────────────────────────────────────────────────────

function OffensiveAbilitiesPanel() {
    const [abilities, setAbilities] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', call: '', cost: ''})
    const [uploadResult, setUploadResult] = useState<{message: string, error: boolean} | null>(null)

    useEffect(() => {
        axios.get('/admin/abilities/offensive').then(({data}) => setAbilities(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', call: '', cost: ''})
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/abilities/offensive', {
                name: form.name, call: form.call, cost: parseInt(form.cost) || 0
            })
            setAbilities(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function uploadCsv(file: File) {
        setUploadResult(null)
        const text = await file.text()
        const rows = parseCsv(text)
        const abilities = rows
            .filter(r => r.length >= 3 && r[0])
            .map(r => ({name: r[0], call: r[1], cost: parseInt(r[2]) || 0}))
        if (abilities.length === 0) {
            setUploadResult({message: 'No valid rows found in CSV.', error: true})
            return
        }
        try {
            const {data} = await axios.post('/admin/abilities/offensive/batch', {abilities})
            const fresh = await axios.get('/admin/abilities/offensive')
            setAbilities(fresh.data)
            setUploadResult({message: `Imported ${data.imported} offensive ${data.imported === 1 ? 'ability' : 'abilities'} (duplicates skipped).`, error: false})
        } catch {
            setUploadResult({message: 'Upload failed. Check CSV format: name,call,cost', error: true})
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/abilities/offensive/${id}`)
        setAbilities(prev => prev.filter(a => a.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', flex: 1, headerAlign: 'center'},
        {field: 'call', headerName: 'Call', flex: 1, headerAlign: 'center'},
        {field: 'cost', headerName: 'Cost', width: 80, headerAlign: 'center', align: 'center'},
        {
            field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <>
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h6" align="center">Offensive Abilities</Typography>
            {uploadResult && (
                <Alert severity={uploadResult.error ? 'error' : 'success'} onClose={() => setUploadResult(null)} sx={{mb: 1}}>
                    {uploadResult.message}
                </Alert>
            )}
            <DataGrid
                autoHeight density="compact"
                rows={abilities} columns={columns}
                getRowId={(r) => r.id}
                slots={{toolbar: () => <QuickFilterToolBar onAdd={() => setOpen(true)} onUpload={uploadCsv}/>}}
                initialState={{pagination: {paginationModel: {pageSize: 15}}}}
            />
        </Paper>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add Offensive Ability</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                                   value={form.name}
                                   onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Call" value={form.call}
                                   onChange={e => setForm({...form, call: e.target.value})}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Cost" type="number" value={form.cost}
                                   inputProps={{min: 0}}
                                   onChange={e => setForm({...form, cost: e.target.value})}/>
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </>
}

// ── Defensive Abilities ──────────────────────────────────────────────────────

function DefensiveAbilitiesPanel() {
    const [abilities, setAbilities] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', call: '', cost: ''})
    const [uploadResult, setUploadResult] = useState<{message: string, error: boolean} | null>(null)

    useEffect(() => {
        axios.get('/admin/abilities/defensive').then(({data}) => setAbilities(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', call: '', cost: ''})
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/abilities/defensive', {
                name: form.name, call: form.call, cost: parseInt(form.cost) || 0
            })
            setAbilities(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function uploadCsv(file: File) {
        setUploadResult(null)
        const text = await file.text()
        const rows = parseCsv(text)
        const abilities = rows
            .filter(r => r.length >= 3 && r[0])
            .map(r => ({name: r[0], call: r[1], cost: parseInt(r[2]) || 0}))
        if (abilities.length === 0) {
            setUploadResult({message: 'No valid rows found in CSV.', error: true})
            return
        }
        try {
            const {data} = await axios.post('/admin/abilities/defensive/batch', {abilities})
            const fresh = await axios.get('/admin/abilities/defensive')
            setAbilities(fresh.data)
            setUploadResult({message: `Imported ${data.imported} defensive ${data.imported === 1 ? 'ability' : 'abilities'} (duplicates skipped).`, error: false})
        } catch {
            setUploadResult({message: 'Upload failed. Check CSV format: name,call,cost', error: true})
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/abilities/defensive/${id}`)
        setAbilities(prev => prev.filter(a => a.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', flex: 1, headerAlign: 'center'},
        {field: 'call', headerName: 'Call', flex: 1, headerAlign: 'center'},
        {field: 'cost', headerName: 'Cost', width: 80, headerAlign: 'center', align: 'center'},
        {
            field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <>
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h6" align="center">Defensive Abilities</Typography>
            {uploadResult && (
                <Alert severity={uploadResult.error ? 'error' : 'success'} onClose={() => setUploadResult(null)} sx={{mb: 1}}>
                    {uploadResult.message}
                </Alert>
            )}
            <DataGrid
                autoHeight density="compact"
                rows={abilities} columns={columns}
                getRowId={(r) => r.id}
                slots={{toolbar: () => <QuickFilterToolBar onAdd={() => setOpen(true)} onUpload={uploadCsv}/>}}
                initialState={{pagination: {paginationModel: {pageSize: 15}}}}
            />
        </Paper>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add Defensive Ability</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                                   value={form.name}
                                   onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Call" value={form.call}
                                   onChange={e => setForm({...form, call: e.target.value})}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Cost" type="number" value={form.cost}
                                   inputProps={{min: 0}}
                                   onChange={e => setForm({...form, cost: e.target.value})}/>
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </>
}

// ── Monstrous Abilities ──────────────────────────────────────────────────────

function MonstrousAbilitiesPanel() {
    const [abilities, setAbilities] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', call: '', type: 'IMMUNITY'})
    const [uploadResult, setUploadResult] = useState<{message: string, error: boolean} | null>(null)

    useEffect(() => {
        axios.get('/admin/abilities/monstrous').then(({data}) => setAbilities(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', call: '', type: 'IMMUNITY'})
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/abilities/monstrous', form)
            setAbilities(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function uploadCsv(file: File) {
        setUploadResult(null)
        const text = await file.text()
        const rows = parseCsv(text)
        const valid = ['IMMUNITY', 'STRENGTH', 'WEAKNESS']
        const abilities = rows
            .filter(r => r.length >= 3 && r[0] && valid.includes(r[2].toUpperCase()))
            .map(r => ({name: r[0], call: r[1], type: r[2].toUpperCase()}))
        if (abilities.length === 0) {
            setUploadResult({message: 'No valid rows found. Type must be IMMUNITY, STRENGTH, or WEAKNESS.', error: true})
            return
        }
        try {
            const {data} = await axios.post('/admin/abilities/monstrous/batch', {abilities})
            const fresh = await axios.get('/admin/abilities/monstrous')
            setAbilities(fresh.data)
            setUploadResult({message: `Imported ${data.imported} monstrous ${data.imported === 1 ? 'ability' : 'abilities'} (duplicates skipped).`, error: false})
        } catch {
            setUploadResult({message: 'Upload failed. Check CSV format: name,call,type', error: true})
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/abilities/monstrous/${id}`)
        setAbilities(prev => prev.filter(a => a.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', flex: 1, headerAlign: 'center'},
        {field: 'call', headerName: 'Call', flex: 1, headerAlign: 'center'},
        {field: 'type', headerName: 'Type', width: 120, headerAlign: 'center', align: 'center'},
        {
            field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <>
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h6" align="center">Monstrous Abilities</Typography>
            {uploadResult && (
                <Alert severity={uploadResult.error ? 'error' : 'success'} onClose={() => setUploadResult(null)} sx={{mb: 1}}>
                    {uploadResult.message}
                </Alert>
            )}
            <DataGrid
                autoHeight density="compact"
                rows={abilities} columns={columns}
                getRowId={(r) => r.id}
                slots={{toolbar: () => <QuickFilterToolBar onAdd={() => setOpen(true)} onUpload={uploadCsv}/>}}
                initialState={{pagination: {paginationModel: {pageSize: 15}}}}
            />
        </Paper>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add Monstrous Ability</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                                   value={form.name}
                                   onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Call" value={form.call}
                                   onChange={e => setForm({...form, call: e.target.value})}/>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="monstrous-type-label">Type</InputLabel>
                            <Select labelId="monstrous-type-label" label="Type" value={form.type}
                                    onChange={e => setForm({...form, type: e.target.value})}>
                                <MenuItem value="IMMUNITY">Immunity</MenuItem>
                                <MenuItem value="STRENGTH">Strength</MenuItem>
                                <MenuItem value="WEAKNESS">Weakness</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !form.call || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </>
}

// ── Intelligence Levels ───────────────────────────────────────────────────────

function IntelligencePanel() {
    const [levels, setLevels] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', description: ''})

    useEffect(() => {
        axios.get('/admin/abilities/intelligence').then(({data}) => setLevels(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', description: ''})
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/abilities/intelligence', form)
            setLevels(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/abilities/intelligence/${id}`)
        setLevels(prev => prev.filter(l => l.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', width: 180, headerAlign: 'center'},
        {field: 'description', headerName: 'Description', flex: 1, headerAlign: 'center'},
        {
            field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <>
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h6" align="center">Intelligence Levels</Typography>
            <DataGrid
                autoHeight density="compact"
                rows={levels} columns={columns}
                getRowId={(r) => r.id}
                slots={{toolbar: () => (
                    <GridToolbarContainer>
                        <GridToolbarQuickFilter/>
                        <Tooltip title="Add intelligence level">
                            <Button onClick={() => setOpen(true)}><PostAddIcon/></Button>
                        </Tooltip>
                    </GridToolbarContainer>
                )}}
                initialState={{pagination: {paginationModel: {pageSize: 15}}}}
            />
        </Paper>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add Intelligence Level</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                                   value={form.name}
                                   onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" multiline minRows={2}
                                   value={form.description}
                                   onChange={e => setForm({...form, description: e.target.value})}/>
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </>
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function MonsterAbilityManager() {
    return <React.Fragment>
        <Grid container spacing={2} p={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">Monster Ability Management</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <OffensiveAbilitiesPanel/>
            </Grid>
            <Grid item xs={12}>
                <DefensiveAbilitiesPanel/>
            </Grid>
            <Grid item xs={12}>
                <MonstrousAbilitiesPanel/>
            </Grid>
            <Grid item xs={12}>
                <IntelligencePanel/>
            </Grid>
        </Grid>
    </React.Fragment>
}
